import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import type { IconType } from '@/lib/icons'
import {
  LayoutDashboard,
  Clapperboard,
  Users,
  Wallet,
  Megaphone,
  BookOpen,
  MessageCircle,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
} from '@/lib/icons'
import type { BloggerStage } from '@ayub/shared'
import { readPref, writePref } from '@/lib/prefs'
import { cn } from '@/lib/utils'

import { ThemeToggle } from './ThemeToggle'
import { BloggerDetail } from './bloggers/BloggerDetail'
import { BloggersPage } from './bloggers/BloggersPage'
import { BooksPage } from './books/BooksPage'
import { BroadcastPage } from './broadcast/BroadcastPage'
import { ChatPage } from './chat/ChatPage'
import { useChatUnreadCount } from './chat/hooks'
import { PayoutsPage } from './payouts/PayoutsPage'
import { SettingsPage } from './settings/SettingsPage'
import { ToastViewport, useToast } from './ui/toast'
import { VideoDetail } from './videos/VideoDetail'
import { VideosPage } from './videos/VideosPage'

export type NavKey =
  'dashboard' | 'videos' | 'bloggers' | 'payouts' | 'chat' | 'broadcast' | 'books' | 'settings'

interface NavItem {
  key: NavKey
  label: string
  icon: IconType
}

// Sahifa 1-6 tartibi, docs/03-app-flow.md "2-QISM — WEBAPP / Navigatsiya" bo'yicha.
export const NAV_ITEMS: readonly NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  // Bosqich 7 da qurildi — `pending` yo'q, pastdagi render mantig'ida alohida
  // ishlanadi (`VideosPage` + `VideoDetail` modali). Prosmotr kiritish (Bosqich 9) va
  // havola tekshiruvi keyingi bosqichlarda shu sahifaning ustiga qo'shiladi.
  { key: 'videos', label: 'Videolar', icon: Clapperboard },
  // Bosqich 6 da qurildi — `pending` yo'q, pastdagi render mantig'ida alohida
  // ishlanadi (`BloggersPage` + `BloggerDetail` modali).
  { key: 'bloggers', label: 'Blogerlar', icon: Users },
  // Bosqich 10 da qurildi — `pending` yo'q, pastdagi render mantig'ida alohida
  // ishlanadi (`PayoutsPage`, o'z modal/tasdiq oynalari bilan).
  { key: 'payouts', label: "To'lovlar", icon: Wallet },
  // Chat — adminlar so'rov yuborgan blogerlar bilan yozishadi (ikki tomonlama, bot orqali).
  { key: 'chat', label: 'Chat', icon: MessageCircle },
  // Bosqich 11 da qurildi — `pending` yo'q, pastdagi render mantig'ida alohida
  // ishlanadi (`BroadcastPage`, o'z ko'rib chiqish/progress oynalari bilan).
  { key: 'broadcast', label: 'Broadcast', icon: Megaphone },
  // Kitob katalogi — mustaqil bo'lim (nom/muallif/narx/izoh), faqat super admin boshqaradi.
  { key: 'books', label: 'Kitoblar', icon: BookOpen },
  // Bosqich 11 da qurildi — `pending` yo'q (`SettingsPage`, adminlar boshqaruvi bilan).
  { key: 'settings', label: 'Sozlamalar', icon: Settings },
]

interface AppShellProps {
  /** `dashboard` bo'limining tarkibi — u yagona tayyor sahifa. */
  children: ReactNode
}

/** Sidebar yig'ilgan holati localStorage kaliti (faqat desktop; `lib/prefs.ts` orqali). */
const SIDEBAR_STORAGE_KEY = 'ayub:sidebar'

export function AppShell({ children }: AppShellProps) {
  // docs/03: "Kirganda birinchi ochiladigan sahifa: Videolar (Kanban) — kundalik ish shu
  // yerda." Bosqich 7 qurilgandan keyin standart qiymat shunga mos o'zgartirildi.
  const [active, setActive] = useState<NavKey>('videos')
  const { toast, showToast } = useToast()

  // Desktop sidebar yig'ilganmi — localStorage'dan tiklanadi, o'zgarishda saqlanadi.
  const [collapsed, setCollapsed] = useState<boolean>(() => readPref(SIDEBAR_STORAGE_KEY) === '1')
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      writePref(SIDEBAR_STORAGE_KEY, next ? '1' : '0')
      return next
    })
  }, [])

  // "Chiqish" endi sidebar'da EMAS — Sozlamalar sahifasidagi `LogoutSection`da (tasodifan
  // bosilmasligi uchun tasdiq oynasi bilan). Bu yerda faqat tema toggle qoladi.

  // Ochilgan bloger/video kartochkasi (`null` = yopiq).
  const [selectedBloggerId, setSelectedBloggerId] = useState<number | null>(null)
  const [selectedVideoId, setSelectedVideoId] = useState<number | null>(null)
  // Chat bo'limida ochiladigan suhbat (kartochkadagi "Chat" tugmasidan).
  const [chatBloggerId, setChatBloggerId] = useState<number | null>(null)

  const unreadCount = useChatUnreadCount()

  const handleSelectBlogger = useCallback((id: number) => {
    setSelectedBloggerId(id)
  }, [])

  /** Kartochkadagi "Chat" — kartochka yopilib, Chat bo'limida o'sha suhbat ochiladi. */
  const handleOpenChat = useCallback((bloggerId: number) => {
    setSelectedBloggerId(null)
    setChatBloggerId(bloggerId)
    setActive('chat')
  }, [])

  const handleSelectVideo = useCallback((id: number) => {
    setSelectedVideoId(id)
  }, [])

  /** Video kartochkasidagi "Bloger kartochkasi →" — video oynasi yopilib, bloger ochiladi. */
  const handleOpenBloggerFromVideo = useCallback((bloggerId: number) => {
    setSelectedVideoId(null)
    setSelectedBloggerId(bloggerId)
  }, [])

  /**
   * Kanbanda kartochkani sudrash — bu bosqichni o'zgartirish SO'ROVI, o'zgarishning
   * o'zi emas.
   *
   * Sabab: rad etish uchun SABAB kerak, bloklash esa tasdiqlashni talab qiladi —
   * ikkalasini ham faqat kartochka oynasi so'ray oladi. Shuning uchun bu yerda
   * `false` qaytariladi (kartochka o'z ustuniga qaytadi) va o'rniga kartochka
   * ochiladi: admin qarorni to'liq kontekst bilan, kerakli maydonlarni to'ldirib
   * qabul qiladi.
   */
  const handleStageChange = useCallback(
    async (bloggerId: number, _newStage: BloggerStage, _previousStage: BloggerStage) => {
      setSelectedBloggerId(bloggerId)
      showToast('Qarorni kartochkada tasdiqlang')
      return false
    },
    [showToast],
  )

  return (
    <div className="flex min-h-dvh flex-col lg:flex-row">
      {/* Desktop: chap sidebar. Yig'ilganda (collapsed) tor bo'lib faqat ikonkalar qoladi. */}
      <aside
        className={cn(
          'hidden shrink-0 flex-col gap-1 border-r border-app-border transition-[width] duration-200 ease-out lg:flex',
          collapsed ? 'p-2 lg:w-16' : 'p-4 lg:w-60',
        )}
      >
        <div
          className={cn(
            'mb-4 flex items-center',
            collapsed ? 'justify-center' : 'justify-between px-2 py-1',
          )}
        >
          {!collapsed && <span className="text-xl font-semibold text-brand">Ayub</span>}
          <button
            type="button"
            onClick={toggleCollapsed}
            title={collapsed ? 'Panelni ochish' : "Panelni yig'ish"}
            aria-label={collapsed ? 'Yon panelni ochish' : "Yon panelni yig'ish"}
            className="flex size-9 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-card hover:text-app-text"
          >
            {collapsed ? (
              <PanelLeftOpen size={18} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={18} aria-hidden="true" />
            )}
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-1">
          {NAV_ITEMS.map((item) => (
            <NavButton
              key={item.key}
              item={item}
              active={active === item.key}
              onSelect={setActive}
              collapsed={collapsed}
              badge={item.key === 'chat' ? unreadCount : 0}
            />
          ))}
        </nav>
        <div className="flex flex-col gap-1">
          <ThemeToggle collapsed={collapsed} />
        </div>
      </aside>

      {/* Mobil: yuqori panel */}
      <header className="flex items-center justify-between border-b border-app-border px-4 py-3 lg:hidden">
        <span className="text-lg font-semibold text-brand">Ayub</span>
        <ThemeToggle collapsed />
      </header>

      <main className="flex-1 overflow-y-auto p-4 pb-24 lg:pb-4">
        {/* Bosqich 11 dan boshlab BARCHA bo'limlar qurilgan — shuning uchun bu yerda
            avvalgi `ComingSoon` shoxobchasi yo'q. `children` faqat `dashboard` uchun
            qoladi (u yagona sahifa bo'lib, tarkibini tashqaridan oladi). */}
        {active === 'videos' ? (
          <VideosPage onSelectVideo={handleSelectVideo} />
        ) : active === 'bloggers' ? (
          <BloggersPage onSelectBlogger={handleSelectBlogger} onStageChange={handleStageChange} />
        ) : active === 'payouts' ? (
          <PayoutsPage />
        ) : active === 'chat' ? (
          <ChatPage initialBloggerId={chatBloggerId} />
        ) : active === 'broadcast' ? (
          <BroadcastPage />
        ) : active === 'books' ? (
          <BooksPage />
        ) : active === 'settings' ? (
          <SettingsPage />
        ) : (
          children
        )}
        <ToastViewport message={toast?.message ?? null} />

        {/* Bloger kartochkasi — ro'yxatdan ham, kanbandan ham, video kartochkasidan ham
            shu bitta oyna ochiladi. */}
        <BloggerDetail
          bloggerId={selectedBloggerId}
          onClose={() => setSelectedBloggerId(null)}
          onOpenChat={handleOpenChat}
        />
        <VideoDetail
          videoId={selectedVideoId}
          onClose={() => setSelectedVideoId(null)}
          onOpenBlogger={handleOpenBloggerFromVideo}
        />
      </main>

      {/* Mobil: pastki tab bar */}
      <nav
        className="fixed inset-x-0 bottom-0 z-10 flex border-t border-app-border bg-app-bg lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {NAV_ITEMS.map((item) => (
          <TabButton
            key={item.key}
            item={item}
            active={active === item.key}
            onSelect={setActive}
            badge={item.key === 'chat' ? unreadCount : 0}
          />
        ))}
      </nav>
    </div>
  )
}

interface NavButtonProps {
  item: NavItem
  active: boolean
  onSelect: (key: NavKey) => void
  /** Yig'ilgan sidebar — faqat ikonka, yorliq yashiriladi (tooltip `title`/`aria-label`da). */
  collapsed?: boolean
  /** O'qilmagan xabar soni (Chat) — 0 bo'lsa belgi ko'rsatilmaydi. */
  badge?: number
}

/** O'qilmagan belgisi — son (99+ gача) yoki ikonka ustidagi nuqta (yig'ilgan/mobil holatda). */
function badgeLabel(count: number): string {
  return count > 99 ? '99+' : String(count)
}

function NavButton({ item, active, onSelect, collapsed = false, badge = 0 }: NavButtonProps) {
  const Icon = item.icon
  return (
    <button
      type="button"
      onClick={() => onSelect(item.key)}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? item.label : undefined}
      aria-label={collapsed ? item.label : undefined}
      className={cn(
        'relative flex min-h-11 items-center rounded-button text-sm font-medium transition-colors duration-150 ease-out',
        collapsed ? 'justify-center px-0' : 'gap-3 px-3',
        active
          ? 'bg-brand-subtle text-brand'
          : 'text-app-hint hover:bg-app-card hover:text-app-text',
      )}
    >
      <span className="relative">
        <Icon size={18} aria-hidden="true" />
        {badge > 0 && collapsed && (
          <span className="absolute -right-1.5 -top-1.5 size-2 rounded-full bg-brand" />
        )}
      </span>
      {!collapsed && item.label}
      {!collapsed && badge > 0 && (
        <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-xs font-semibold text-white">
          {badgeLabel(badge)}
        </span>
      )}
    </button>
  )
}

function TabButton({ item, active, onSelect, badge = 0 }: NavButtonProps) {
  const Icon = item.icon
  return (
    <button
      type="button"
      onClick={() => onSelect(item.key)}
      aria-current={active ? 'page' : undefined}
      aria-label={item.label}
      className={cn(
        'flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 text-[0.6875rem] font-medium',
        active ? 'text-brand' : 'text-app-hint',
      )}
    >
      <span className="relative">
        <Icon size={20} aria-hidden="true" />
        {badge > 0 && (
          <span className="absolute -right-2 -top-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[0.625rem] font-semibold leading-4 text-white">
            {badgeLabel(badge)}
          </span>
        )}
      </span>
      <span>{item.label}</span>
    </button>
  )
}
